import { useState, type FormEvent } from 'react';
import { collection, orderBy, query } from 'firebase/firestore';
import { UserPlus } from 'lucide-react';
import { callable, db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { useDocs } from '@/lib/useDocs';
import { messageForAuthError } from '@/lib/authErrors';
import { normalizePhoneInput } from '@/lib/phone';
import { formatDate, roleLabel } from '@/lib/format';
import { ROLES } from '@/types/roles';
import type { UserRecord } from '@/types/domain';
import { strings } from '@/strings';

const setRoles = callable<{ uid: string; roles: string[] }, { uid: string; roles: string[] }>('setRoles');
const createUserByPhone = callable<{ phone: string; name: string }, { uid: string; phone: string }>('createUserByPhone');

/**
 * An invite decides a role at first sign-in; this changes it afterwards. Those
 * two are the complete set of ways anyone becomes anything, because no client
 * can write users/ and sign-in never assigns a role on its own.
 *
 * Your own row shows why it has no control instead of offering one that fails:
 * setRole refuses a self-change, since an admin demoting themselves can remove
 * the last admin and leave nobody able to promote anyone back.
 */
export function UsersPage() {
  const { user } = useAuth();
  const { rows, loading, error } = useDocs<UserRecord>(
    query(collection(db, 'users'), orderBy('createdAt', 'desc')),
    []
  );
  const [choice, setChoice] = useState<Record<string, string[]>>({});
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const [newPhone, setNewPhone] = useState('');
  const [newName, setNewName] = useState('');
  const [addProblem, setAddProblem] = useState<string | null>(null);
  const [added, setAdded] = useState(false);
  const canonical = normalizePhoneInput(newPhone);

  async function add(event: FormEvent) {
    event.preventDefault();
    if (!canonical || !newName.trim() || busy) return;
    setBusy(true);
    setAddProblem(null);
    setAdded(false);
    try {
      await createUserByPhone({ phone: canonical, name: newName.trim() });
      setNewPhone('');
      setNewName('');
      setAdded(true);
    } catch (err) {
      const code = (err as Error).message;
      // Two callables throw `name-required` meaning two different names, and
      // the shared map answers for the nomination form. This page answers for
      // its own, the way InvitesPage does with `already-registered`.
      // Both codes are thrown by more than one callable and mean something
      // different in each. The shared map answers for the nomination form and
      // for organization registration; this page answers for its own.
      if (code.includes('name-required')) setAddProblem(strings.errNameRequiredUser);
      else if (code.includes('already-registered')) setAddProblem(strings.errUserAlreadyRegistered);
      else setAddProblem(messageForAuthError(code));
    } finally {
      setBusy(false);
    }
  }

  /** The roles a row currently shows, which is its stored set until edited. */
  function rolesOf(row: UserRecord & { id: string }): string[] {
    return choice[row.id] ?? (row.roles && row.roles.length > 0 ? row.roles : [row.role]);
  }

  function toggle(row: UserRecord & { id: string }, role: string) {
    const current = rolesOf(row);
    const next = current.includes(role) ? current.filter((r) => r !== role) : [...current, role];
    // Everyone holds at least one role: `guest` is the one that means no
    // powers, and an empty set is a user no rule can reason about.
    if (next.length === 0) return;
    setChoice((prev) => ({ ...prev, [row.id]: next }));
  }

  function changed(row: UserRecord & { id: string }): boolean {
    const stored = row.roles && row.roles.length > 0 ? row.roles : [row.role];
    const next = rolesOf(row);
    return next.length !== stored.length || next.some((r) => !stored.includes(r));
  }

  async function apply(uid: string, roles: string[]) {
    if (busy) return;
    setBusy(true);
    setProblem(null);
    try {
      await setRoles({ uid, roles });
    } catch (err) {
      setProblem(messageForAuthError((err as Error).message));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex max-w-3xl flex-col gap-6">
      <h1 className="text-xl font-bold text-primary">{strings.usersTitle}</h1>

      {/* Phone and name only. The role is not asked for here on purpose: it is
          set below through setRole, the one door a role changes through and the
          only one that records who granted what. */}
      <form onSubmit={add} className="flex flex-col gap-3 rounded-lg border border-border-subtle bg-white p-4">
        <div className="flex items-center gap-2">
          <UserPlus className="h-4 w-4 shrink-0 text-muted" aria-hidden="true" />
          <span className="font-semibold text-ink">{strings.userAddTitle}</span>
        </div>
        <p className="text-xs text-muted">{strings.userAddHint}</p>

        <div className="flex flex-col gap-3 md:flex-row md:items-end">
          <label className="flex flex-1 flex-col gap-1 text-sm" htmlFor="new-user-phone">
            {strings.signInPhoneLabel}
            <input
              id="new-user-phone"
              inputMode="tel"
              dir="ltr"
              value={newPhone}
              onChange={(e) => setNewPhone(e.target.value)}
              placeholder={strings.signInPhonePlaceholder}
              className="text-right"
            />
          </label>
          <label className="flex flex-1 flex-col gap-1 text-sm" htmlFor="new-user-name">
            {strings.inviteName}
            <input id="new-user-name" value={newName} onChange={(e) => setNewName(e.target.value)} />
          </label>
          <button
            type="submit"
            disabled={!canonical || !newName.trim() || busy}
            className="min-h-11 shrink-0 min-h-11 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary-dark disabled:cursor-not-allowed disabled:opacity-50"
          >
            {strings.userAddSubmit}
          </button>
        </div>

        {added ? <p className="text-sm text-success">{strings.userAdded}</p> : null}
        {addProblem ? <p role="alert" className="text-sm text-danger">{addProblem}</p> : null}
      </form>
      {loading ? <p className="text-muted">{strings.loading}</p> : null}
      {error ? <p role="alert" className="text-danger">{strings.loadFailed}</p> : null}
      {!loading && !error && rows.length === 0 ? <p className="text-muted">{strings.usersEmpty}</p> : null}

      <ul className="flex flex-col gap-2">
        {rows.map((row) => {
          const isMe = row.id === user?.uid;
          const picked = rolesOf(row);
          return (
            <li
              key={row.id}
              data-testid={`user-${row.id}`}
              className="flex flex-col gap-3 rounded-lg border border-border-subtle bg-white p-4 md:flex-row md:items-center md:justify-between"
            >
              <div className="flex flex-col gap-1">
                <span className="font-medium text-ink">
                  {row.name || '—'}{isMe ? ` (${strings.userYou})` : ''}
                </span>
                <span className="text-sm text-muted" dir="ltr">{row.phone}</span>
                <span className="text-xs text-muted" dir="ltr">{formatDate(row.createdAt)}</span>
                <span className="text-xs text-muted">
                  {(row.roles && row.roles.length > 0 ? row.roles : [row.role]).map(roleLabel).join(' · ')}
                </span>
              </div>

              {isMe ? (
                <span className="text-sm text-muted">{strings.userCannotChangeSelf}</span>
              ) : (
                <div className="flex flex-col items-start gap-3 md:items-end">
                  {/* Checkboxes, not a select: a person may hold several roles,
                      and a single-choice control cannot say so. */}
                  <fieldset className="flex flex-wrap gap-x-4 gap-y-2">
                    <legend className="sr-only">
                      {`${strings.userChangeRole} ${row.name || row.phone}`}
                    </legend>
                    {ROLES.map((r) => (
                      <label key={r} className="flex min-h-11 items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={picked.includes(r)}
                          onChange={() => toggle(row, r)}
                          aria-label={`${roleLabel(r)} ${row.name || row.phone}`}
                        />
                        {roleLabel(r)}
                      </label>
                    ))}
                  </fieldset>
                  <button
                    type="button"
                    disabled={busy || !changed(row)}
                    onClick={() => apply(row.id, picked)}
                    className="min-h-11 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary-dark disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {strings.userChangeRole}
                  </button>
                </div>
              )}
            </li>
          );
        })}
      </ul>
      {problem ? <p role="alert" className="text-sm text-danger">{problem}</p> : null}
    </div>
  );
}
